/**
 * Packing Workflow Simulator
 * Simulates the complete packing workflow using real API calls
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { VirtualOperator } from '../services/VirtualOperator';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

export interface PackingWorkflowResult {
  success: boolean;
  groupingId?: string;
  ordersProcessed: number;
  packagesProcessed: number;
  error?: string;
  duration: number;
}

export class PackingWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();

  constructor(config: SimulatorConfig, operator: VirtualOperator) {
    this.config = config;
    this.operator = operator;
  }

  /**
   * Execute one complete packing workflow cycle
   */
  async execute(): Promise<PackingWorkflowResult> {
    const startTime = Date.now();
    let groupingId: string | undefined;
    let ordersProcessed = 0;
    let packagesProcessed = 0;

    try {
      // Step 1: Get packing groups
      this.operator.startTask('packing', 'Henter pakkegrupper');
      await this.rateLimiter.throttle();

      const packingGroups = await this.getPackingGroups();

      if (packingGroups.length === 0) {
        return {
          success: true,
          ordersProcessed: 0,
          packagesProcessed: 0,
          duration: Date.now() - startTime,
          error: 'Ingen pakkegrupper tilgjengelig'
        };
      }

      // Step 2: Select a random group to process
      const selectedGroup = packingGroups[Math.floor(Math.random() * packingGroups.length)];
      groupingId = selectedGroup.groupingId ?? selectedGroup.GroupingId ?? selectedGroup.groupingID;

      if (!groupingId) {
        return {
          success: false,
          ordersProcessed: 0,
          packagesProcessed: 0,
          duration: Date.now() - startTime,
          error: 'Kunne ikke finne groupingId i valgt gruppe'
        };
      }

      this.operator.startTask('packing', `Behandler gruppe ${groupingId}`);
      await this.rateLimiter.throttle();

      // Step 3: Get group details
      const groupDetails = await this.getPackingGroupDetails(groupingId);
      console.log(`[PackingWorkflow] Group details for ${groupingId}:`, JSON.stringify(groupDetails, null, 2).substring(0, 500));

      // Handle both camelCase and PascalCase from API
      const groupOrders = groupDetails.orders ?? groupDetails.Orders ?? [];
      ordersProcessed = groupOrders.length;
      console.log(`[PackingWorkflow] Found ${ordersProcessed} orders in group details`);

      // Step 4: Get or create shipment
      this.operator.startTask('packing', `Henter/oppretter forsendelse for ${groupingId}`);
      await this.rateLimiter.throttle();

      const shipment = await this.getOrCreateShipment(groupingId);
      console.log(`[PackingWorkflow] Shipment ${shipment.shipmentID}:`, JSON.stringify(shipment, null, 2).substring(0, 500));

      // Step 5: Simulate packing process
      this.operator.startTask('packing', `Pakker forsendelse ${shipment.shipmentID}`);
      await this.delay(this.config.timing.packTimeMs);

      // Step 6: Create packages for the shipment (required before finalization)
      // Get orders from the shipment or group details - handle both camelCase and PascalCase
      const shipmentOrders = shipment.orders ?? shipment.Orders ?? [];
      const orders = shipmentOrders.length > 0 ? shipmentOrders : groupOrders;
      console.log(`[PackingWorkflow] Processing ${orders.length} orders for packaging`);

      if (orders.length > 0) {
        // Create 1-2 packages per order to simulate realistic packing
        for (const order of orders) {
          const orderNo = order.orderNo ?? order.OrderNo ?? order.orderno ?? 0;
          const pListId = order.pListID ?? order.PListID ?? order.plistId ?? 0;

          // Both PListID and OrderNo are required by the API
          if (pListId > 0 && orderNo > 0) {
            this.operator.startTask('packing', `Oppretter pakker for ordre ${orderNo} (PList ${pListId})`);
            await this.rateLimiter.throttle();

            const numberOfPackages = Math.floor(Math.random() * 2) + 1; // 1-2 packages

            try {
              const createResult = await this.createPackageLabels({
                numberOfLabels: numberOfPackages,
                pListID: pListId,
                orderNo: orderNo,
                pickType: 'Standalone', // String, not number!
                printLabels: false // Don't print in simulator
              });

              if (createResult.success) {
                packagesProcessed += createResult.packagesCreated || numberOfPackages;
                console.log(`[PackingWorkflow] Created ${createResult.packagesCreated || numberOfPackages} packages for order ${orderNo}`);
              } else {
                console.warn(`[PackingWorkflow] createPackageLabels returned success=false for order ${orderNo}: ${createResult.message}`);
              }
            } catch (pkgError) {
              console.warn(`[PackingWorkflow] Could not create packages for order ${orderNo}: ${pkgError}`);
              // Continue with other orders
            }
          } else {
            console.warn(`[PackingWorkflow] Skipping order - missing PListID (${pListId}) or OrderNo (${orderNo})`);
          }
        }
      } else {
        console.warn(`[PackingWorkflow] No orders found in group ${groupingId}`);
      }

      // Step 7: Update shipment with number of packages
      if (packagesProcessed > 0) {
        this.operator.startTask('packing', `Oppdaterer forsendelse med ${packagesProcessed} kolli`);
        await this.rateLimiter.throttle();
        await this.updateShipment(shipment.shipmentID, {
          numOfPackages: packagesProcessed
        });
      }

      // Step 8: If package checkout is required, confirm packages
      const packageStatus = await this.getPackageConfirmationStatus(groupingId);
      if (packageStatus.packages && packageStatus.packages.length > 0) {
        for (const pkg of packageStatus.packages) {
          if (!pkg.isConfirmed) {
            // API returns these fields from PackageForConfirmationDto
            const pkgId = pkg.packageId ?? pkg.PackageId;
            const pListId = pkg.pListID ?? pkg.PListID ?? pkg.plistId;
            const orderNo = pkg.orderNo ?? pkg.OrderNo;
            const sourceType = pkg.sourceType ?? pkg.SourceType ?? 'Standalone';

            if (pkgId !== undefined && pListId !== undefined) {
              this.operator.startTask('packing', `Bekrefter pakke ${pkgId}`);
              await this.rateLimiter.throttle();
              await this.confirmPackage(shipment.shipmentID, pkgId, pListId, orderNo, sourceType);
              await this.delay(500); // Short delay between confirmations
            } else {
              console.warn('[PackingWorkflow] Package missing required fields:', JSON.stringify(pkg));
            }
          }
        }
      }

      // Step 9: Finalize shipment
      this.operator.startTask('packing', `Avslutter forsendelse ${shipment.shipmentID}`);
      await this.rateLimiter.throttle();
      await this.finalizeShipment(shipment.shipmentID);

      return {
        success: true,
        groupingId,
        ordersProcessed,
        packagesProcessed,
        duration: Date.now() - startTime
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[PackingWorkflow] Error:`, errorMessage);

      return {
        success: false,
        groupingId,
        ordersProcessed,
        packagesProcessed,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Get packing groups
   */
  private async getPackingGroups(): Promise<any[]> {
    const response = await authenticatedFetch(`${API_BASE_URL}/packing/groups`);

    if (!response.ok) {
      throw new Error(`Failed to fetch packing groups: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get packing group details
   */
  private async getPackingGroupDetails(groupingId: string): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/packing/groups/${encodeURIComponent(groupingId)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch packing group details: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get or create shipment
   */
  private async getOrCreateShipment(groupingId: string): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/packing/shipment/${encodeURIComponent(groupingId)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get shipment: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get package confirmation status
   */
  private async getPackageConfirmationStatus(groupingId: string): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/packing/packages/${encodeURIComponent(groupingId)}`
    );

    if (!response.ok) {
      // Package confirmation might not be enabled - return empty
      return { packages: [] };
    }

    return response.json();
  }

  /**
   * Confirm a package
   * API requires: ShipmentID, ScannedPackageId, PListID, OrderNo, SourceType
   */
  private async confirmPackage(
    shipmentId: number,
    packageId: number,
    pListId: number,
    orderNo: number | undefined,
    sourceType: string
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/packing/packages/confirm`,
      {
        method: 'POST',
        body: JSON.stringify({
          ShipmentID: shipmentId,
          ScannedPackageId: packageId,
          PListID: pListId,
          OrderNo: orderNo,
          SourceType: sourceType
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Failed to confirm package: ${response.statusText}`);
    }
  }

  /**
   * Create package labels for an order
   * API requires: NumberOfLabels, PListID, OrderNo, PickType (string: "Standalone" or "Batch")
   */
  private async createPackageLabels(params: {
    numberOfLabels: number;
    pListID: number;
    orderNo: number;
    pickType: string; // "Standalone" or "Batch"
    printLabels: boolean;
  }): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/packing/packages/create-and-print`,
      {
        method: 'POST',
        body: JSON.stringify({
          NumberOfLabels: params.numberOfLabels,
          PListID: params.pListID,
          OrderNo: params.orderNo,
          PickType: params.pickType,
          PrintLabels: params.printLabels
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Failed to create package labels: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update shipment details
   * IMPORTANT: This must succeed before finalizeShipment can work (NumOfPackages required)
   */
  private async updateShipment(shipmentID: number, updates: {
    numOfPackages?: number;
  }): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/packing/shipment/${shipmentID}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          NumOfPackages: updates.numOfPackages
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(`Failed to update shipment ${shipmentID}: ${error.message || response.statusText}`);
    }
  }

  /**
   * Finalize shipment
   */
  private async finalizeShipment(shipmentID: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/packing/shipment/${shipmentID}/finalize`,
      {
        method: 'POST',
        body: JSON.stringify({})
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to finalize shipment: ${response.statusText}`);
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default PackingWorkflow;
